/**
 * Payment method types the Payment Element can confirm that the `PaymentMethod` enum has no case
 * for: the backend records them as `stripe_other` and keeps Stripe's raw type in
 * `stripe_payment_method_type`. Types that do have a case (Klarna, TWINT, iDEAL, …) never reach
 * here, the backend names them by their case.
 *
 * Artwork is Shopify's MIT-licensed payment_icons pack (activemerchant/payment_icons), kept as the
 * pack ships it and named after the Stripe type; `generic.svg` stands in for a type without one.
 */
const ICON_BY_FILE = import.meta.glob<string>('../../assets/icons/payment-methods/stripe/*.svg', {
  eager: true,
  query: '?url',
  import: 'default',
});

const iconFor = (name: string): string | undefined =>
  ICON_BY_FILE[`../../assets/icons/payment-methods/stripe/${name}.svg`];

const NAME_BY_TYPE: Record<string, string> = {
  acss_debit: 'Pre-authorized debit',
  affirm: 'Affirm',
  afterpay_clearpay: 'Afterpay',
  alipay: 'Alipay',
  alma: 'Alma',
  amazon_pay: 'Amazon Pay',
  au_becs_debit: 'BECS Direct Debit',
  bacs_debit: 'Bacs Direct Debit',
  billie: 'Billie',
  bizum: 'Bizum',
  boleto: 'Boleto',
  cashapp: 'Cash App Pay',
  crypto: 'Crypto',
  customer_balance: 'Bank transfer',
  fpx: 'FPX',
  grabpay: 'GrabPay',
  kakao_pay: 'Kakao Pay',
  konbini: 'Konbini',
  kr_card: 'Korean card',
  mb_way: 'MB WAY',
  mobilepay: 'MobilePay',
  multibanco: 'Multibanco',
  naver_pay: 'Naver Pay',
  nz_bank_account: 'NZ BECS Direct Debit',
  oxxo: 'OXXO',
  payco: 'PAYCO',
  paynow: 'PayNow',
  promptpay: 'PromptPay',
  revolut_pay: 'Revolut Pay',
  samsung_pay: 'Samsung Pay',
  satispay: 'Satispay',
  scalapay: 'Scalapay',
  swish: 'Swish',
  upi: 'UPI',
  us_bank_account: 'ACH Direct Debit',
  wechat_pay: 'WeChat Pay',
  zip: 'Zip',
};

export const GENERIC_PAYMENT_METHOD_ICON = iconFor('generic')!;

export function stripePaymentMethodTypeIcon(type: string | null | undefined): string {
  return (type && iconFor(type)) || GENERIC_PAYMENT_METHOD_ICON;
}

/** Null without a type, so the caller can fall back to its own "other payment method" label. */
export function stripePaymentMethodTypeName(type: string | null | undefined): string | null {
  if (!type) {
    return null;
  }

  const humanized = type.replaceAll('_', ' ');

  return NAME_BY_TYPE[type] ?? humanized.charAt(0).toUpperCase() + humanized.slice(1);
}
